/**
 * Choosing which Drive files a bulk import brings in, and which wardrobe
 * folders the resulting garments join - plus how that choice turned out once
 * the worker walked the Drive folder.
 *
 * The rules below mirror the create endpoint's 422s (see the backend's
 * `bulk_import_manifest` and `drive_patterns`), so an operator hears about a
 * malformed pattern or a clashing folder name before queueing rather than as
 * an opaque rejection. The server stays the authority: whatever it still
 * refuses is shown verbatim.
 */

import type {
  WardrobeBulkImportFolderRequest,
  WardrobeBulkImportMemberStatus,
} from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Badge } from '@ui/components/base/badges/badges';
import { Callout } from '@ui/console/primitives';
import { CheckboxField, TextAreaField } from '@ui/console/forms';
import { Quantity, formatCount } from '@ui/console/measures';
import { errorDetail } from './operation-policy';

/* ------------------------------------------------------------------ rules */

export const MAX_FOLDER_NAME = 80;
const MAX_PATTERN_LENGTH = 300;
const PLACEHOLDERS = new Set(['folder', 'path']);
const PLACEHOLDER = /\{([^{}]*)\}/g;
const DRIVE_ID = /^[A-Za-z0-9_-]{5,256}$/;
const DRIVE_FILE_URL =
  /^https:\/\/drive\.google\.com\/(?:file\/d\/([A-Za-z0-9_-]{5,256})(?:\/.*)?|open\?id=([A-Za-z0-9_-]{5,256})(?:&.*)?)$/;

/** A Drive file link or id, as the server tells it apart from a path pattern. */
export function isDriveFileReference(value: string): boolean {
  const candidate = value.trim();
  if (/[*?]/.test(candidate)) return false;
  return DRIVE_ID.test(candidate) || DRIVE_FILE_URL.test(candidate);
}

/** Whether a non-reference entry is a pattern the server can compile. */
function patternIsWellFormed(pattern: string): boolean {
  const cleaned = pattern.trim().replace(/^\/+|\/+$/g, '');
  if (!cleaned || cleaned.length > MAX_PATTERN_LENGTH) return false;
  return cleaned.split('/').every((segment) => segment.length > 0);
}

function collapse(name: string): string {
  return name.split(/\s+/).filter(Boolean).join(' ');
}

export function isFolderTemplate(name: string): boolean {
  return new RegExp(PLACEHOLDER.source).test(name);
}

/** What `folders_from_subfolders` stands for on the server. */
const SUBFOLDER_FOLDER = { name: '{folder}', pattern: '*/**' } as const;
const ARROW = '->';

export type ParsedManifest = {
  /** Plain lines: what the import brings in. Empty means the whole Drive folder. */
  files: string[];
  /** `ref -> name` lines, merged by name (case-insensitively), in first-seen order. */
  folders: WardrobeBulkImportFolderRequest[];
  /** One sentence per line that cannot be sent as written. */
  problems: string[];
};

/**
 * One manifest entry per line:
 *
 *   `Tops/*.jpg`             imports what the link, id or pattern selects
 *   `Shoes/** -> Shoes`      also files what it selects into the “Shoes” folder
 *   `Tops/** -> {folder}`    files each garment into its top-level Drive subfolder
 *
 * Blank lines and `#` comments are skipped. Names are compared the way the
 * server compares them: runs of whitespace collapsed, case ignored.
 */
export function parseManifest(text: string): ParsedManifest {
  const files: string[] = [];
  const folders: WardrobeBulkImportFolderRequest[] = [];
  const byName = new Map<string, WardrobeBulkImportFolderRequest>();
  const problems: string[] = [];
  text.split(/\r?\n/).forEach((raw, index) => {
    const line = raw.trim();
    if (!line || line.startsWith('#')) return;
    const where = `Line ${index + 1}`;
    const parts = line.split(ARROW);
    if (parts.length > 2) {
      problems.push(`${where}: use “${ARROW}” only once, between the file and the folder name.`);
      return;
    }
    const ref = (parts[0] ?? '').trim();
    if (!referenceIsUsable(ref)) {
      problems.push(
        ref
          ? `${where}: “${ref}” is neither a Drive file link or id nor a usable pattern. Patterns cannot be longer than ${MAX_PATTERN_LENGTH} characters or contain an empty part such as “//”.`
          : `${where}: put a file link, id or pattern before “${ARROW}”.`,
      );
      return;
    }
    if (parts.length === 1) {
      if (!files.includes(ref)) files.push(ref);
      return;
    }
    const name = collapse(parts[1] ?? '');
    const problem = folderNameProblem(name);
    if (problem) {
      problems.push(`${where}: ${problem}`);
      return;
    }
    const key = name.toLowerCase();
    const existing = byName.get(key);
    if (existing) {
      if (!existing.files.includes(ref)) existing.files.push(ref);
      return;
    }
    const folder = { name, files: [ref] };
    byName.set(key, folder);
    folders.push(folder);
  });
  return { files, folders, problems };
}

function referenceIsUsable(ref: string): boolean {
  return Boolean(ref) && (isDriveFileReference(ref) || patternIsWellFormed(ref));
}

function folderNameProblem(name: string): string | undefined {
  if (!name) return `put a folder name after “${ARROW}”.`;
  if (name.length > MAX_FOLDER_NAME)
    return `the folder name “${name}” is longer than ${MAX_FOLDER_NAME} characters.`;
  const unknown = [...name.matchAll(PLACEHOLDER)]
    .map((match) => match[1] ?? '')
    .filter((placeholder) => !PLACEHOLDERS.has(placeholder));
  const stray = name.replace(PLACEHOLDER, '');
  if (unknown.length || stray.includes('{') || stray.includes('}'))
    return `“${name}” can only use {folder} and {path} in curly brackets.`;
  return undefined;
}

export type SelectionDraft = {
  folderLink: string;
  manifest: string;
  foldersFromSubfolders: boolean;
  /** Use a Drive folder's name as a garment-type hint for the model. */
  categoryFromFolderNames?: boolean;
};

export type Selection = {
  body: {
    drive_folder_link?: string;
    file_links_or_ids?: string[];
    folders?: WardrobeBulkImportFolderRequest[];
    folders_from_subfolders?: boolean;
    category_from_folder_names?: boolean;
  };
  /** Everything the create endpoint would refuse with a 422, in the operator's words. */
  problems: string[];
  /** How many entries it imports (0: the whole folder) and the folders it files into. */
  imports: number;
  folderNames: string[];
};

/**
 * The request the draft makes. The subfolder checkbox and a manifest
 * `-> {folder}` line are the same folder, so only one of them is sent: the
 * alias alone, or the manifest's entry with the alias's pattern folded in -
 * which is exactly how the server would merge the two.
 */
export function buildSelection(draft: SelectionDraft): Selection {
  const parsed = parseManifest(draft.manifest);
  const folders = parsed.folders.map((folder) => ({ ...folder, files: [...folder.files] }));
  let alias = draft.foldersFromSubfolders;
  if (alias) {
    const explicit = folders.find((folder) => folder.name.toLowerCase() === SUBFOLDER_FOLDER.name);
    if (explicit) {
      if (!explicit.files.includes(SUBFOLDER_FOLDER.pattern))
        explicit.files.push(SUBFOLDER_FOLDER.pattern);
      alias = false;
    }
  }
  const problems = [...parsed.problems];
  const selectsByPath =
    draft.foldersFromSubfolders ||
    parsed.files.some((entry) => !isDriveFileReference(entry)) ||
    folders.some(
      (folder) =>
        isFolderTemplate(folder.name) || folder.files.some((entry) => !isDriveFileReference(entry)),
    );
  const link = draft.folderLink.trim();
  if (!link) {
    if (selectsByPath)
      problems.push(
        'Patterns and folders pick files inside a Drive folder, so add the Drive folder link.',
      );
    else if (!parsed.files.length)
      problems.push('Add the Drive folder link, or list the Drive files to import.');
  }
  const folderNames = [
    ...folders.map((folder) => folder.name),
    ...(alias ? [SUBFOLDER_FOLDER.name] : []),
  ];
  return {
    body: {
      ...(link ? { drive_folder_link: link } : {}),
      ...(parsed.files.length ? { file_links_or_ids: parsed.files } : {}),
      ...(folders.length ? { folders } : {}),
      ...(alias ? { folders_from_subfolders: true } : {}),
      ...(draft.categoryFromFolderNames ? { category_from_folder_names: true } : {}),
    },
    problems,
    imports: parsed.files.length,
    folderNames,
  };
}

/**
 * The server's refusal, word for word. FastAPI's own validation errors arrive
 * as a list rather than a sentence; those are joined so they read as one.
 */
export function rejectionDetail(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'detail' in error) {
    const { detail } = error;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((entry) => {
          if (typeof entry !== 'object' || entry === null) return undefined;
          const { msg, loc } = entry as { msg?: unknown; loc?: unknown };
          if (typeof msg !== 'string') return undefined;
          const where = Array.isArray(loc) ? loc.filter((part) => part !== 'body').join('.') : '';
          return where ? `${where}: ${msg}` : msg;
        })
        .filter((message): message is string => Boolean(message));
      if (messages.length) return messages.join('; ');
    }
  }
  return errorDetail(error, fallback);
}

/* ------------------------------------------------------------------- form */

const plural = (count: number, one: string, many: string) =>
  `${formatCount(count)} ${count === 1 ? one : many}`;

export function ManifestField({
  value,
  onChange,
  selection,
}: {
  value: string;
  onChange: (value: string) => void;
  selection: Selection;
}) {
  const { imports, folderNames } = selection;
  const touched = value.trim().length > 0 || folderNames.length > 0;
  return (
    <div className="flex flex-col gap-1.5">
      <TextAreaField
        label="Manifest (optional)"
        hint="One per line: a Drive file link, a file id or a pattern, such as Tops/*.jpg, IMG_12* or **/*.png. Add “-> folder name” to put those garments into a folder, as in Shoes/** -> Shoes or */** -> {folder}. Leave blank to import every image."
        rows={6}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {touched ? (
        <p className="text-xs text-secondary" aria-live="polite" data-testid="manifest-preview">
          {imports ? plural(imports, 'import', 'imports') : 'Every image in the Drive folder'},{' '}
          {plural(folderNames.length, 'folder', 'folders')}
          {folderNames.length ? `: ${folderNames.join(', ')}` : ''}
        </p>
      ) : null}
      <details className="text-xs text-tertiary">
        <summary className="cursor-pointer font-medium text-secondary">
          How patterns and folders work
        </summary>
        <ul className="flex list-disc flex-col gap-1 pt-2 pl-5">
          <li>
            <code>*</code> stands for any run of characters and <code>?</code> for one character,
            both within a single folder level. <code>**</code> stands for any number of folder
            levels, so <code>**/*.png</code> is every PNG anywhere.
          </li>
          <li>
            Without a <code>/</code>, a pattern looks at the file name wherever the file is:{' '}
            <code>IMG_12*</code> finds IMG_1201.jpg in any subfolder. With a <code>/</code>, it is a
            path from the Drive folder: <code>Tops/*.jpg</code> is the JPGs directly inside Tops.
          </li>
          <li>
            Capital letters do not matter. To name one exact file, include its extension
            (IMG_1201.jpg); a bare word without one is read as a Drive file id.
          </li>
          <li>
            A line with <code>{ARROW}</code> chooses a folder; lines naming the same folder are
            combined. It does not narrow the import: if every line has <code>{ARROW}</code>, every
            image is still imported and only the matching ones go into folders.
          </li>
          <li>
            In a folder name, <code>{'{folder}'}</code> becomes the top-level Drive subfolder a file
            is in (“Tops”) and <code>{'{path}'}</code> its whole subfolder path (“Tops / Shirts”).
            Words around them are kept, as in “Summer {'{folder}'}”.
          </li>
          <li>
            Lines starting with <code>#</code> are notes and are skipped.
          </li>
        </ul>
      </details>
    </div>
  );
}

export function SubfolderFoldersField({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <CheckboxField
      label="Create a folder for each Drive folder"
      hint="Each subfolder directly inside the Drive folder becomes a wardrobe folder of the same name, holding the garments from it and anything below it. An existing folder with that name is reused; images sitting loose in the Drive folder get no folder."
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

export function CategoryFromFolderNamesField({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <CheckboxField
      label="Use Drive folder names as a garment-type hint"
      hint="A folder named for one garment type, such as “Heavy Saree”, tells the model the images are probably that type; it still goes by the image. The closest folder that names a type is used, up to the Drive folder itself. Images already in the wardrobe are not re-categorised or reprocessed now; they keep the hint for their next reprocess."
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

/* ----------------------------------------------------------------- status */

const QUOTED = String.raw`(?:'((?:[^'\\]|\\.)*)'|"((?:[^"\\]|\\.)*)")`;
const NO_MATCH = new RegExp(`^pattern ${QUOTED} matched no files$`);
const NO_SUBFOLDER = new RegExp(`^folder ${QUOTED} resolved to no Drive subfolder$`);

/**
 * A member's failure reason as sentences. The discovery step reports every
 * pattern that found nothing and every folder template that named nothing,
 * joined with "; "; those are rephrased, anything else is kept as written.
 */
export function explainFailureReason(reason: string): string[] {
  return reason
    .split('; ')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const noMatch = NO_MATCH.exec(part);
      if (noMatch)
        return `No file in the Drive folder matches “${noMatch[1] ?? noMatch[2] ?? ''}”. Check the spelling and the subfolder names.`;
      const noSubfolder = NO_SUBFOLDER.exec(part);
      if (noSubfolder)
        return `The folder “${noSubfolder[1] ?? noSubfolder[2] ?? ''}” could not be named: every file it covers sits directly in the Drive folder, not in a subfolder.`;
      return part;
    });
}

/** Whether this reason came from the file-selection check that runs first. */
function isSelectionFailure(reason: string): boolean {
  return reason.split('; ').some((part) => NO_MATCH.test(part) || NO_SUBFOLDER.test(part));
}

export function MemberFailure({ reason }: { reason: string }) {
  const sentences = explainFailureReason(reason);
  const rephrased = sentences.join(' ') !== reason;
  return (
    <Callout
      tone="error"
      title={
        isSelectionFailure(reason)
          ? 'Nothing was imported: the file choices did not match the Drive folder.'
          : 'This member’s import failed.'
      }
    >
      <div className="flex flex-col gap-2">
        {sentences.length > 1 ? (
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {sentences.map((sentence) => (
              <li key={sentence}>{sentence}</li>
            ))}
          </ul>
        ) : (
          <p>{sentences[0] ?? reason}</p>
        )}
        {rephrased ? (
          <details className="text-xs">
            <summary className="cursor-pointer">Exact message</summary>
            <p className="pt-1 font-mono break-words">{reason}</p>
          </details>
        ) : null}
      </div>
    </Callout>
  );
}

/** Whether a member chose files by pattern or asked for folders at all. */
export function memberUsesSelection(member: WardrobeBulkImportMemberStatus): boolean {
  return Boolean(
    member.file_patterns?.length ||
    member.folder_specs?.length ||
    member.folders?.length ||
    Object.keys(member.pattern_matches ?? {}).length,
  );
}

/**
 * What a member's file patterns and folders came to. Renders nothing for an
 * import that used neither, so a plain whole-folder import looks as before.
 */
export function MemberSelectionOutcome({
  member,
  foldersHref,
}: {
  member: WardrobeBulkImportMemberStatus;
  foldersHref?: string | undefined;
}) {
  const patterns = member.file_patterns ?? [];
  const specs = member.folder_specs ?? [];
  const folders = member.folders ?? [];
  const matches = member.pattern_matches ?? {};
  // Every pattern the member asked for, including those only a folder used.
  const allPatterns = Array.from(
    new Set([
      ...patterns,
      ...specs.flatMap((spec) => spec.files.filter((entry) => !isDriveFileReference(entry))),
      ...Object.keys(matches),
    ]),
  );
  if (!allPatterns.length && !specs.length && !folders.length) return null;
  const discovered = Object.keys(matches).length > 0 || folders.length > 0;
  const zero = allPatterns.filter((pattern) => matches[pattern] === 0);

  return (
    <div className="flex flex-col gap-4">
      {specs.length || folders.length ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h4 className="text-sm font-semibold text-primary">Folders</h4>
            {foldersHref && folders.length ? (
              <Button size="sm" color="link-color" href={foldersHref}>
                Open the member’s folders
              </Button>
            ) : null}
          </div>
          {folders.length ? (
            <ul
              aria-label="Folders"
              className="flex flex-col divide-y divide-secondary rounded-lg border border-secondary"
            >
              {folders.map((folder) => (
                <li
                  key={`${folder.spec}:${folder.name}`}
                  className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2"
                >
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-primary">{folder.name}</span>
                    {folder.spec !== folder.name ? (
                      <span className="text-xs text-tertiary">From “{folder.spec}”</span>
                    ) : null}
                  </span>
                  <span className="text-sm text-secondary">
                    <Quantity
                      value={formatCount(folder.matched_files)}
                      raw={folder.matched_files}
                    />{' '}
                    {folder.matched_files === 1 ? 'file' : 'files'} matched,{' '}
                    <Quantity
                      value={formatCount(folder.added_garments)}
                      raw={folder.added_garments}
                    />{' '}
                    {folder.added_garments === 1 ? 'garment' : 'garments'} added so far
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-tertiary">
              {member.status === 'failed'
                ? 'No folders were made.'
                : 'Folders appear here once the Drive folder has been read, a few seconds after queueing.'}{' '}
              Asked for:{' '}
              {specs.map((spec, index) => (
                <span key={spec.name}>
                  {index ? ', ' : ''}“{spec.name}”
                </span>
              ))}
              .
            </p>
          )}
        </div>
      ) : null}
      {allPatterns.length ? (
        <details className="flex flex-col gap-2" open={zero.length > 0}>
          <summary className="cursor-pointer text-sm font-semibold text-primary">
            Pattern matches
            {zero.length ? (
              <span className="ml-2 text-sm font-normal text-error-primary">
                {zero.length === 1 ? '1 pattern' : `${zero.length} patterns`} matched nothing
              </span>
            ) : null}
          </summary>
          <ul
            aria-label="Pattern matches"
            className="mt-2 flex flex-col divide-y divide-secondary rounded-lg border border-secondary"
          >
            {allPatterns.map((pattern) => {
              const count = matches[pattern];
              return (
                <li
                  key={pattern}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <code className="text-sm break-all text-primary">{pattern}</code>
                  {count === undefined ? (
                    <span className="text-sm text-tertiary">
                      {discovered ? 'Not checked' : 'Waiting for the Drive folder to be read'}
                    </span>
                  ) : count === 0 ? (
                    <Badge type="pill-color" size="sm" color="error">
                      No matches
                    </Badge>
                  ) : (
                    <span className="text-sm text-secondary">
                      <Quantity value={formatCount(count)} raw={count} />{' '}
                      {count === 1 ? 'file' : 'files'}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
